import { useConversation } from "../../../Stores/Conversation";
import { useAppState } from "../../../Stores/AppState";
import { useChat } from "../../Chat/Store/Chat";
import { useTeam } from "../../Custom/Team/Store/Team";
import { useUnlinkedPanelState } from "../../../Stores/useUnlinkedPanelState";
import { useLinkedPanelState } from "../../../Stores/useLinkedPanelState";
import { useDivisionStore } from "../../../Stores/Division";
import { useRightDrawerStore } from "../../../Stores/RightDrawer";
import { useAuthorityLevelStore } from "../../../Stores/AuthorityLevel";
// import { useUserStore } from "../../../Stores/User";
import { useAlignStore } from "../../../Stores/Align";
import { useReportsStore } from "../../../Stores/Reports";
import { useRolesAndPermissions } from "../../../Stores/RolesAndPermissions";
import utilities from "../../../Helpers/utilities";
import { applyCloseOrganizationReportsPanelState } from "./organizationReportsPanelClose.js";
import { applyCloseRightDrawerModulesForMembershipOpen } from "./rightDrawerModulesForMembershipOpen.js";

function isMembershipSubpanelActive() {
    const app_state_store = useAppState();
    const app_chat = useChat();

    // Message tab means membership was already closed — ignore stale variant/tab state.
    if (app_chat.is_message_tab) {
        return false;
    }

    if (app_chat.is_member_tab) {
        return true;
    }

    if (app_chat.is_new_member_tab) {
        return true;
    }

    if (app_chat.is_new_workpanel_member_tab) {
        return true;
    }

    // Dashboard can be opened while membership is still active (member → dashboard).
    // Rely on conv_id_before_open_membership so stale members variant/tab alone does not count.
    // Standalone associate dashboard is not membership even when conv_id is saved for restore.
    if (app_chat.is_dashboard_tab) {
        if (app_chat.standalone_associate_profile_id) {
            return false;
        }

        return app_chat.conv_id_before_open_membership !== null;
    }

    if (
        app_state_store.selected_variant_tab === "members" ||
        app_state_store.tab === "members"
    ) {
        return true;
    }

    return false;
}

/**
 * True when a membership subpanel is open, regardless of conversation type
 * (e.g. DM on the members tab while the team drawer is still open).
 */
export function isActiveMembershipSubpanelView() {
    return isMembershipSubpanelActive();
}

/**
 * True when the directory (team) subpanel is open and not obscured by membership.
 */
export function isDirectorySubpanelView() {
    const app_state_store = useAppState();

    return (
        (Boolean(app_state_store.invite_drawer?.is_team_opened) ||
            Boolean(app_state_store.invite_drawer?.is_new_directory_opened)) &&
        !isMembershipSubpanelActive()
    );
}

function isGroupOrTopicConversation(active) {
    if (!active) {
        return false;
    }

    return (
        active.is_group ||
        active.is_topic ||
        active.is_group_one_off ||
        (typeof active.isGroup === "function" && active.isGroup())
    );
}

/**
 * True when the membership subpanel is open for a directory group or topic conversation.
 */
export function isDirectoryGroupOrTopicMembershipView() {
    const conversation_store = useConversation();
    const active = conversation_store.active;

    return isGroupOrTopicConversation(active) && isMembershipSubpanelActive();
}

/**
 * True when the membership subpanel is open for an item or item-group conversation.
 */
export function isItemOrItemGroupMembershipView() {
    const conversation_store = useConversation();
    const active = conversation_store.active;

    if (!active) {
        return false;
    }

    const is_item_or_item_group =
        active.is_item ||
        active.is_item_group ||
        (typeof active.isItem === "function" && active.isItem());

    return is_item_or_item_group && isMembershipSubpanelActive();
}

/**
 * True when the membership subpanel is open for a board or board-group conversation.
 */
export function isBoardOrBoardGroupMembershipView() {
    const conversation_store = useConversation();
    const active = conversation_store.active;

    if (!active) {
        return false;
    }

    const is_board_or_board_group =
        active.is_board ||
        active.is_board_group ||
        (typeof active.isBoard === "function" && active.isBoard());

    return is_board_or_board_group && isMembershipSubpanelActive();
}

/**
 * True when the membership subpanel is open for a space or space-group conversation.
 */
export function isSpaceOrSpaceGroupMembershipView() {
    const conversation_store = useConversation();
    const active = conversation_store.active;

    if (!active) {
        return false;
    }

    const is_space_or_space_group =
        active.is_space ||
        active.is_space_group ||
        (typeof active.isSpace === "function" && active.isSpace());

    return is_space_or_space_group && isMembershipSubpanelActive();
}

/**
 * True when the item dashboard is open for an item conversation.
 */
export function isItemDashboardView() {
    const conversation_store = useConversation();
    const app_chat = useChat();

    if (!app_chat.is_dashboard_tab) {
        return false;
    }

    // Standalone associate dashboard keeps the prior item as active.
    if (isAssociateDashboardView()) {
        return false;
    }

    const active = conversation_store.active;

    if (!active) {
        return false;
    }

    const is_item =
        active.is_item ||
        (typeof active.isItem === "function" && active.isItem());

    return is_item;
}

/**
 * True when the associate dashboard is open (standalone session or DM with an associate).
 */
export function isAssociateDashboardView() {
    const conversation_store = useConversation();
    const app_chat = useChat();

    if (!app_chat.is_dashboard_tab) {
        return false;
    }

    if (app_chat.standalone_associate_profile_id) {
        return true;
    }

    const active = conversation_store.active;

    if (!active) {
        return false;
    }

    const is_dm =
        active.is_dm || (typeof active.isDm === "function" && active.isDm());

    const is_agent_conversation =
        typeof active.isAgentConversation === "function" &&
        active.isAgentConversation();

    return is_dm && !is_agent_conversation;
}

/**
 * True when the associate settings (worker settings) panel is open.
 */
export function isAssociateSettingsView() {
    const app_chat = useChat();

    return app_chat.is_worker_settings_tab;
}

/**
 * True when a Setup (Align) panel step is open in the right drawer.
 */
export function isSetupPanelView() {
    const right_drawer_store = useRightDrawerStore();

    return right_drawer_store.isAlignActive;
}

function closeSetupPanelState() {
    const right_drawer_store = useRightDrawerStore();
    const align_store = useAlignStore();

    if (!right_drawer_store.isAlignActive) {
        return;
    }

    right_drawer_store.closeDrawer();
    align_store.setSelectedStep(null);
}

/**
 * True when a Workday settings controls panel is open in the right drawer.
 */
export function isWorkdayControlsPanelView() {
    const right_drawer_store = useRightDrawerStore();

    return (
        right_drawer_store.isWorkdayActive ||
        right_drawer_store.isOfficeHoursActive ||
        right_drawer_store.isTimezonesActive
    );
}

function closeWorkdayControlsPanelState() {
    const right_drawer_store = useRightDrawerStore();
    const linked_panel_state = useLinkedPanelState();
    const roles_and_permissions_store = useRolesAndPermissions();

    if (
        right_drawer_store.isWorkdayActive ||
        right_drawer_store.isOfficeHoursActive ||
        right_drawer_store.isTimezonesActive
    ) {
        right_drawer_store.closeDrawer();
    }

    linked_panel_state.clearActiveWorkdayPanel();
    roles_and_permissions_store.isWorkdayStartPermission = false;
    roles_and_permissions_store.isWorkdayStopPermission = false;
}

function resolveConversationAfterClosingStandaloneAssociate() {
    const conversation_store = useConversation();
    const app_chat = useChat();
    const previous_conv_id = app_chat.conv_id_before_standalone_associate;

    if (previous_conv_id === null) {
        return;
    }

    const active = conversation_store.active;

    if (active && typeof active.isFake === "function" && active.isFake()) {
        conversation_store.openConversationOfType(active.type, {
            id: active.type_id,
            name: active.name,
        });
        return;
    }

    conversation_store.openConversationById(previous_conv_id);
}

function closeStandaloneAssociateSessionState() {
    const app_chat = useChat();
    const authority_level_store = useAuthorityLevelStore();

    const standalone_profile_id = app_chat.standalone_associate_profile_id;

    if (!standalone_profile_id) {
        return;
    }

    app_chat.clearStandaloneAssociateProfileId();
    app_chat.clearAssociateDashboard();

    if (
        String(authority_level_store.selectedProfileId ?? "") ===
        String(standalone_profile_id)
    ) {
        authority_level_store.setSelectedProfileId(null);
    }

    resolveConversationAfterClosingStandaloneAssociate();
    app_chat.clearConvIdBeforeStandaloneAssociate();
}

async function closeAssociateDashboardState() {
    const app_chat = useChat();

    if (!app_chat.is_dashboard_tab) {
        return;
    }

    if (app_chat.standalone_associate_profile_id) {
        closeStandaloneAssociateSessionState();
    } else {
        app_chat.clearAssociateDashboard();
    }
}

async function closeAssociateSettingsState() {
    const app_chat = useChat();

    if (!app_chat.is_worker_settings_tab) {
        return;
    }

    app_chat.is_fetching_associate_settings = false;
    app_chat.worker_settings_data_loading = false;
    app_chat.setWorkerSettingsReportsToActive(false);
    app_chat.clearWorkerSettingsToolbarOpenProfileId();

    if (app_chat.standalone_associate_profile_id) {
        closeStandaloneAssociateSessionState();
    }
}

function resolveConversationAfterClosingMembership() {
    const conversation_store = useConversation();
    const app_chat = useChat();
    const previous_conv_id = app_chat.conv_id_before_open_membership;

    if (previous_conv_id === null) {
        return;
    }

    conversation_store.openConversationById(previous_conv_id);
}

/**
 * Matches TeamMembersActions.closeInviteChat when closing conversation membership
 * (not the directory team drawer).
 */
function closeConversationMembershipLikeCancel() {
    const app_state_store = useAppState();
    const app_chat = useChat();
    const team_store = useTeam();

    if (app_chat.is_member_tab) {
        app_chat.toggleMemberTab();
    }

    if (app_chat.is_new_member_tab) {
        app_chat.toggleNewMemberTab();
    }

    if (app_chat.is_new_workpanel_member_tab) {
        app_chat.toggleNewWorkPanelMemberTab();
    }

    team_store.setNewVariantName(null);
    app_state_store.setLockedSelectedVariantTab(null);
    app_state_store.resetSelectedVariantTab();
}

function closeMembershipSubpanelState() {
    const app_state_store = useAppState();
    const app_chat = useChat();
    const team_store = useTeam();
    const conversation_store = useConversation();
    const active = conversation_store.active;

    if (
        !app_state_store.invite_drawer?.is_team_opened &&
        isGroupOrTopicConversation(active)
    ) {
        closeConversationMembershipLikeCancel();
        // Same as the full close path — stale conv_id makes dashboard tab look like membership is still open.
        app_chat.setPreviousConvID(null);
        return;
    }

    resolveConversationAfterClosingMembership();

    team_store.setNewVariantName(null);
    app_chat.setPreviousConvID(null);
    app_state_store.setLockedSelectedVariantTab(null);
    app_state_store.resetSelectedVariantTab();
    app_state_store.setTab("all");
}

function closeDirectoryPanelState() {
    const conversation_store = useConversation();
    const app_state_store = useAppState();
    const unlinkedPanelState = useUnlinkedPanelState();

    if (app_state_store.invite_drawer?.is_new_directory_opened) {
        app_state_store.toggleNewDirectoryPanel(false);
    }

    if (!app_state_store.invite_drawer?.is_team_opened) {
        return;
    }

    conversation_store.toggleProductivityPanel(false);
    conversation_store.toggleTeamPanel(false, conversation_store.active);
    unlinkedPanelState.openDirectoryPanelTracked(false);
    app_state_store.handleMembershipPanel();
}

function closeProductivityPanelState() {
    const conversation_store = useConversation();
    const app_state_store = useAppState();
    const unlinkedPanelState = useUnlinkedPanelState();

    if (!app_state_store.productivity_drawer?.is_productivity_opened) {
        return;
    }

    conversation_store.toggleTeamPanel(false, conversation_store.active);
    conversation_store.toggleProductivityPanel(false);
}

/**
 * Shows the conversation panel (message tab) and keeps the chat popover open.
 *
 * @param {{ isHiddenRef?: import('vue').Ref<boolean> }} [options]
 */
function showConversationPanel(options = {}) {
    const app_state_store = useAppState();
    const app_chat = useChat();
    const unlinkedPanelState = useUnlinkedPanelState();
    const { toggleChatPopover } = utilities();

    unlinkedPanelState.restrictRecoveryMode();
    app_state_store.invite_drawer.is_workday_opened = false;
    app_state_store.invite_drawer.is_workday_permission_opened = false;

    app_chat.setMessageTab();
    app_chat.setPreviousTab("message");
    app_chat.openConversation();
    toggleChatPopover(true);

    if (options.isHiddenRef) {
        options.isHiddenRef.value = false;
    }

    if (unlinkedPanelState.savedRoutes.chat?.name) {
        unlinkedPanelState.switchToLastDetachChatRouteFromUnlinkedPanel();
    }
}

/**
 * True when the global directory invite panel (right drawer) is open.
 */
export function isDirectoryInviteSubpanelView() {
    const right_drawer_store = useRightDrawerStore();

    return right_drawer_store.isDirectoryInviteActive;
}

function closeDirectoryInvitePanelState() {
    const right_drawer_store = useRightDrawerStore();

    if (!right_drawer_store.isDirectoryInviteActive) {
        return;
    }

    right_drawer_store.closeDrawer();
}

/**
 * True when the Organization Reports panel (right drawer) is open.
 */
export function isOrganizationReportsPanelView() {
    const right_drawer_store = useRightDrawerStore();

    return right_drawer_store.isReportsActive;
}

export function closeOrganizationReportsPanel() {
    const right_drawer_store = useRightDrawerStore();
    const reports_store = useReportsStore();

    applyCloseOrganizationReportsPanelState({
        isReportsActive: right_drawer_store.isReportsActive,
        closeDrawer: () => right_drawer_store.closeDrawer(),
        setSelectedReport: (report) => reports_store.setSelectedReport(report),
        setShowRecipients: (value) => {
            reports_store.showRecipients = value;
        },
    });
}

/**
 * True when Organization Admin Controls or Associate Controls is open.
 */
export function isOrganizationControlsPanelView() {
    const right_drawer_store = useRightDrawerStore();

    return (
        right_drawer_store.isAdministrationActive ||
        right_drawer_store.isInviterAdminActive ||
        right_drawer_store.isCanSetEveryoneMandateActive ||
        right_drawer_store.isCanSetDownlineMandateActive ||
        right_drawer_store.isCanViewEveryoneScreenshotsActive ||
        right_drawer_store.isCanViewDownlineScreenshotsActive
    );
}

function closeOrganizationControlsPanelState() {
    const right_drawer_store = useRightDrawerStore();
    const linked_panel_state = useLinkedPanelState();
    const app_state_store = useAppState();

    if (
        right_drawer_store.isAdministrationActive ||
        right_drawer_store.isInviterAdminActive ||
        right_drawer_store.isCanSetEveryoneMandateActive ||
        right_drawer_store.isCanSetDownlineMandateActive ||
        right_drawer_store.isCanViewEveryoneScreenshotsActive ||
        right_drawer_store.isCanViewDownlineScreenshotsActive
    ) {
        right_drawer_store.closeDrawer();
    }

    linked_panel_state.clearActiveOrganizationPanel();
    app_state_store.setRoleObject(null);
    app_state_store.is_manage_organization_role_opened = false;
}

/**
 * Closes the directory (team) subpanel and shows the conversation panel (message tab).
 *
 * @param {{ isHiddenRef?: import('vue').Ref<boolean> }} [options]
 */
export function closeDirectoryAndOpenConversation(options = {}) {
    closeDirectoryPanelState();
    showConversationPanel(options);
}

/**
 * Closes the productivity subpanel and shows the conversation panel (message tab).
 *
 * @param {{ isHiddenRef?: import('vue').Ref<boolean> }} [options]
 */
export function closeProductivityAndOpenConversation(options = {}) {
    closeProductivityPanelState();
    showConversationPanel(options);
}

/**
 * Closes membership for the active conversation and shows the conversation panel (message tab).
 *
 * @param {{ isHiddenRef?: import('vue').Ref<boolean> }} [options]
 */
export function closeMembershipAndOpenConversation(options = {}) {
    closeMembershipSubpanelState();
    closeProductivityPanelState();
    closeDirectoryPanelState();
    showConversationPanel(options);
}

/**
 * Closes the item dashboard and shows the conversation panel (message tab).
 *
 * @param {{ isHiddenRef?: import('vue').Ref<boolean> }} [options]
 */
export function closeDashboardAndOpenConversation(options = {}) {
    closeProductivityPanelState();
    closeDirectoryPanelState();
    showConversationPanel(options);
}

/**
 * Closes the associate dashboard and shows the conversation panel (message tab).
 *
 * @param {{ isHiddenRef?: import('vue').Ref<boolean> }} [options]
 */
export async function closeAssociateDashboardAndOpenConversation(options = {}) {
    closeProductivityPanelState();
    closeDirectoryPanelState();
    await closeAssociateDashboardState();
    showConversationPanel(options);
}

/**
 * Closes the associate settings panel and shows the conversation panel (message tab).
 *
 * @param {{ isHiddenRef?: import('vue').Ref<boolean> }} [options]
 */
export async function closeAssociateSettingsAndOpenConversation(options = {}) {
    closeProductivityPanelState();
    closeDirectoryPanelState();
    await closeAssociateSettingsState();
    showConversationPanel(options);
}

/**
 * Closes the Setup (Align) panel step and shows the conversation panel (message tab).
 *
 * @param {{ isHiddenRef?: import('vue').Ref<boolean> }} [options]
 */
export function closeSetupPanelAndOpenConversation(options = {}) {
    closeProductivityPanelState();
    closeDirectoryPanelState();
    closeSetupPanelState();
    showConversationPanel(options);
}

/**
 * Closes Workday settings controls (general workday, office hours, timezone) and shows the conversation panel.
 *
 * @param {{ isHiddenRef?: import('vue').Ref<boolean> }} [options]
 */
export function closeWorkdayControlsAndOpenConversation(options = {}) {
    closeWorkdayControlsPanelState();
    closeProductivityPanelState();
    closeDirectoryPanelState();
    showConversationPanel(options);
}

/**
 * True when the Add or Edit Seniority panel (authority level drawer) is open.
 */
export function isSeniorityPanelView() {
    const right_drawer_store = useRightDrawerStore();

    return right_drawer_store.isAuthorityLevelActive;
}

function closeSeniorityPanelState() {
    const right_drawer_store = useRightDrawerStore();

    if (right_drawer_store.isAuthorityLevelActive) {
        right_drawer_store.closeDrawer();
    }
}

/**
 * Closes the Add or Edit Seniority panel and shows the conversation panel (message tab).
 *
 * @param {{ isHiddenRef?: import('vue').Ref<boolean> }} [options]
 */
export function closeSeniorityPanelAndOpenConversation(options = {}) {
    closeSeniorityPanelState();
    closeProductivityPanelState();
    closeDirectoryPanelState();
    showConversationPanel(options);
}

function closeDivisionAndMemberSelectRightDrawerState() {
    const divisionStore = useDivisionStore();
    const rightDrawerStore = useRightDrawerStore();
    const app_state_store = useAppState();

    const was_member_select = rightDrawerStore.isMemberSelectActive;
    const was_division = rightDrawerStore.isDivisionActive;

    if (!was_member_select && !was_division) {
        return;
    }

    divisionStore.setShowMembership(false);
    rightDrawerStore.closeDrawer();

    if (was_member_select) {
        app_state_store.resetMemberSelect();
    }
}

/**
 * Closes far-right drawer modules so membership can show in the conversation panel.
 * Does not change the chat tab or call showConversationPanel().
 */
export function closeCompetingRightDrawerModules() {
    applyCloseRightDrawerModulesForMembershipOpen({
        closeOrganizationReports: () => closeOrganizationReportsPanel(),
        closeOrganizationControls: () => closeOrganizationControlsPanelState(),
        closeWorkdayControls: () => closeWorkdayControlsPanelState(),
        closeSetupPanel: () => closeSetupPanelState(),
        closeDirectoryInvite: () => closeDirectoryInvitePanelState(),
        closeSeniority: () => closeSeniorityPanelState(),
        closeDivisionAndMemberSelect: () =>
            closeDivisionAndMemberSelectRightDrawerState(),
    });
}

/** @deprecated Use {@link closeCompetingRightDrawerModules}. */
export function closeRightDrawerModulesForMembershipOpen() {
    closeCompetingRightDrawerModules();
}

/**
 * Closes the Organization panel division controls drawer and shows the conversation panel.
 *
 * @param {{ isHiddenRef?: import('vue').Ref<boolean> }} [options]
 */
export function closeDivisionControlsAndOpenConversation(options = {}) {
    closeDivisionAndMemberSelectRightDrawerState();
    closeProductivityPanelState();
    closeDirectoryPanelState();
    showConversationPanel(options);
}

/**
 * Closes the global directory invite panel and shows the conversation panel (message tab).
 *
 * @param {{ isHiddenRef?: import('vue').Ref<boolean> }} [options]
 */
export function closeDirectoryInviteAndOpenConversation(options = {}) {
    closeDirectoryInvitePanelState();
    showConversationPanel(options);
}

/**
 * Closes the Organization Reports panel and shows the conversation panel (message tab).
 *
 * @param {{ isHiddenRef?: import('vue').Ref<boolean> }} [options]
 */
export function closeOrganizationReportsPanelAndOpenConversation(options = {}) {
    closeOrganizationReportsPanel();
    closeProductivityPanelState();
    closeDirectoryPanelState();
    showConversationPanel(options);
}

/**
 * Closes Organization Admin Controls or Associate Controls without changing the conversation panel.
 */
export function closeOrganizationControlsPanel() {
    closeOrganizationControlsPanelState();
}

/**
 * Closes Organization Admin Controls or Associate Controls and shows the conversation panel.
 *
 * @param {{ isHiddenRef?: import('vue').Ref<boolean> }} [options]
 */
export function closeOrganizationControlsAndOpenConversation(options = {}) {
    closeOrganizationControlsPanelState();
    closeProductivityPanelState();
    closeDirectoryPanelState();
    showConversationPanel(options);
}
